"use client";

import * as React from "react";
import { useActionState } from "react";
import { Button } from "@modernatc/ui/button";
import { Input } from "@modernatc/ui/input";
import { Label } from "@modernatc/ui/label";
import { Callsign } from "@modernatc/ui/callsign";
import {
  submitInstitutionInquiry,
  type InquiryResult,
} from "@/app/actions/institution-inquiry";

/**
 * Institutional inquiry form. Mirrors the waitlist UX (server action
 * via React 19 `useActionState`), but with full contact + use-case
 * fields tailored to a bulk-licensing conversation.
 *
 * Why a textarea for "message" instead of a structured intent picker?
 * The institutional pipeline is too varied to pre-classify (AT-CTI
 * programs, military, FBO training departments, individual instructor
 * groups). A textarea collects more useful signal than a dropdown.
 */
export function InstitutionForm() {
  const [state, formAction, isPending] = useActionState<InquiryResult | null, FormData>(
    submitInstitutionInquiry,
    null,
  );

  if (state?.ok) {
    return (
      <div className="rounded-[var(--radius-md)] border border-[var(--color-clear)]/40 bg-[var(--color-panel)] p-8">
        <Callsign className="text-[11px] text-[var(--color-clear)]">
          ▸ Confirmed
        </Callsign>
        <h3 className="mt-3 text-xl font-semibold text-[var(--color-ink)]">
          We&rsquo;ll be in touch.
        </h3>
        <p className="mt-2 text-pretty text-[var(--color-ink-dim)]">
          A real human reads every inquiry. Expect a reply within two
          business days, sooner during the week if you&rsquo;re a class
          coordinator with a near-term cohort.
        </p>
      </div>
    );
  }

  // Generic error message when the action returned ok=false with no field.
  const generalError =
    state && !state.ok && !state.field ? state.error : null;
  const fieldError = (name: string): string | null =>
    state && !state.ok && state.field === name ? state.error : null;

  return (
    <form
      action={formAction}
      className="panel-grain rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] p-8 shadow-[var(--shadow-panel)]"
    >
      <Callsign className="text-[10px] text-[var(--color-amber)]">
        ▸ Inquiry form
      </Callsign>
      <h3 className="mt-2 text-2xl font-semibold tracking-tight text-[var(--color-ink)]">
        Bulk licensing & partner pilots
      </h3>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Field
          name="organization"
          label="Organization"
          required
          autoComplete="organization"
          error={fieldError("organization")}
        />
        <Field
          name="contactName"
          label="Your name"
          required
          autoComplete="name"
          error={fieldError("contactName")}
        />
        <Field
          name="email"
          label="Work email"
          type="email"
          required
          autoComplete="email"
          error={fieldError("email")}
        />
        <Field
          name="role"
          label="Role / title"
          autoComplete="organization-title"
          error={fieldError("role")}
        />
        <Field
          name="seats"
          label="Estimated seats"
          placeholder="e.g. 24 students, 6 instructors"
          error={fieldError("seats")}
          className="sm:col-span-2"
        />
      </div>

      <div className="mt-5">
        <Label htmlFor="message" className="mb-2 block">
          What are you hoping to do?
        </Label>
        <textarea
          id="message"
          name="message"
          rows={5}
          placeholder="Cohort timeline, current curriculum, what's missing from your existing tools…"
          className="block w-full resize-y rounded-[var(--radius)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] px-3 py-2 text-sm text-[var(--color-ink)] shadow-inner placeholder:text-[var(--color-ink-muted)] focus:border-[var(--color-amber)] focus:outline-none"
        />
        {fieldError("message") ? (
          <p className="mt-1 text-xs text-[var(--color-critical)]">
            {fieldError("message")}
          </p>
        ) : null}
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-callsign text-[10px] text-[var(--color-ink-muted)]">
          ▸ We&rsquo;ll never share your contact info or solicit on it.
        </p>
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? "Sending…" : "Send inquiry"}
        </Button>
      </div>

      {generalError ? (
        <p
          role="alert"
          className="mt-4 text-sm text-[var(--color-critical)]"
        >
          {generalError}
        </p>
      ) : null}
    </form>
  );
}

interface FieldProps {
  name: string;
  label: string;
  required?: boolean;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
  error?: string | null;
  className?: string;
}

function Field({
  name,
  label,
  required,
  type = "text",
  autoComplete,
  placeholder,
  error,
  className,
}: FieldProps) {
  const id = `inst-${name}`;
  return (
    <div className={className}>
      <Label htmlFor={id} className="mb-2 block">
        {label}
        {required ? (
          <span aria-hidden className="ml-1 text-[var(--color-amber)]">
            *
          </span>
        ) : null}
      </Label>
      <Input
        id={id}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
      />
      {error ? (
        <p id={`${id}-err`} className="mt-1 text-xs text-[var(--color-critical)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
